import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BatchControls = ({ 
  queueStatus = 'idle', // idle, processing, paused, completed
  onStart,
  onPause,
  onResume,
  onStop,
  workerCount = 2,
  onWorkerCountChange,
  apiProvider = 'gemini',
  onApiProviderChange,
  geminiModel = 'gemini-2.0-flash',
  onGeminiModelChange = () => {},
  hasFiles = false,
  className = '' 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const workerOptions = [
    { value: 1, label: '1 Worker (Slow)' },
    { value: 2, label: '2 Workers (Recommended)' },
    { value: 3, label: '3 Workers (Fast)' },
    { value: 4, label: '4 Workers (Maximum)' }
  ];

  const apiProviderOptions = [
    { 
      value: 'gemini', 
      label: 'Gemini 2.0 Flash',
      description: 'Primary OCR engine with high accuracy'
    },
    { 
      value: 'mistral', 
      label: 'Mistral OCR',
      description: 'Secondary engine for reprocessing'
    }
  ];

  const geminiModelOptions = [
    {
      value: 'gemini-2.0-flash',
      label: 'Gemini 2.0 Flash (Stable)',
      description: 'Stable release optimized for production workloads'
    },
    {
      value: 'gemini-2.0-flash-exp',
      label: 'Gemini 2.0 Flash (Experimental)',
      description: 'Fastest responses with latest improvements'
    }
  ];

  const geminiModelLabelMap = geminiModelOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

  const getOcrEngineLabel = () => {
    if (apiProvider === 'gemini') {
      const label = geminiModelLabelMap[geminiModel] || 'Gemini 2.0 Flash';
      return label;
    }
    return 'Mistral OCR';
  };

  const getStatusConfig = () => {
    switch (queueStatus) {
      case 'processing':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          icon: 'Play',
          text: 'Processing'
        };
      case 'paused':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Pause',
          text: 'Paused'
        };
      case 'completed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          text: 'Completed'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'Clock',
          text: 'Ready'
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center
            ${statusConfig?.bgColor}
          `}>
            <Icon 
              name={statusConfig?.icon} 
              size={16} 
              className={`${statusConfig?.color} ${queueStatus === 'processing' ? 'animate-pulse' : ''}`}
            />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">Batch Processing</h3>
            <p className="text-xs text-muted-foreground">
              Status: <span className={statusConfig?.color}>{statusConfig?.text}</span>
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-smooth"
        >
          <span>Advanced</span>
          <Icon 
            name={showAdvanced ? "ChevronUp" : "ChevronDown"} 
            size={14} 
          />
        </button>
      </div>
      {/* Main Controls */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {queueStatus === 'idle' && (
              <Button
                variant="default"
                onClick={onStart}
                disabled={!hasFiles}
                iconName="Play"
                iconPosition="left"
              >
                Start Processing
              </Button>
            )}

            {queueStatus === 'processing' && (
              <>
                <Button
                  variant="outline"
                  onClick={onPause}
                  iconName="Pause"
                  iconPosition="left"
                >
                  Pause
                </Button>
                <Button
                  variant="destructive"
                  onClick={onStop}
                  iconName="Square"
                  iconPosition="left"
                >
                  Stop
                </Button>
              </>
            )}

            {queueStatus === 'paused' && (
              <>
                <Button
                  variant="default"
                  onClick={onResume}
                  iconName="Play"
                  iconPosition="left"
                >
                  Resume
                </Button>
                <Button
                  variant="destructive"
                  onClick={onStop}
                  iconName="Square"
                  iconPosition="left"
                >
                  Stop
                </Button>
              </>
            )}

            {queueStatus === 'completed' && (
              <Button
                variant="outline"
                onClick={() => window.location.href = '/invoices'}
                iconName="FileText"
                iconPosition="left"
              >
                View Invoices
              </Button>
            )}
          </div>

          {!hasFiles && queueStatus === 'idle' && (
            <p className="text-xs text-muted-foreground">
              Add files to queue to start processing
            </p>
          )}
        </div>

        {/* Advanced Settings */}
        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Worker Concurrency"
                description="Number of parallel processing workers"
                options={workerOptions}
                value={workerCount}
                onChange={onWorkerCountChange}
                disabled={queueStatus === 'processing'}
              />

              <Select
                label="OCR Provider"
                description="Primary OCR engine for processing"
                options={apiProviderOptions}
                value={apiProvider}
                onChange={onApiProviderChange}
                disabled={queueStatus === 'processing'}
              />
            </div>

            {apiProvider === 'gemini' && (
              <Select
                label="Gemini Model"
                description="Choose between experimental or stable Gemini variants"
                options={geminiModelOptions}
                value={geminiModel}
                onChange={onGeminiModelChange}
                disabled={queueStatus === 'processing'}
              />
            )}

            {/* Processing Info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>
                    <strong>Worker Concurrency:</strong> Higher values process files faster but use more API quota.
                  </p>
                  <p>
                    <strong>OCR Provider:</strong> Gemini 2.0 Flash is recommended for initial processing. 
                    Use Mistral for reprocessing low-confidence extractions.
                  </p>
                  <p>
                    <strong>Gemini Models:</strong> Experimental mode delivers the newest capabilities, while the stable model prioritizes consistency. Switch depending on your QA needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Processing Stats */}
      {queueStatus !== 'idle' && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="text-sm font-semibold text-foreground">{workerCount}</div>
              <div className="text-xs text-muted-foreground">Active Workers</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="text-sm font-semibold text-primary">{getOcrEngineLabel()}</div>
              <div className="text-xs text-muted-foreground">OCR Engine</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="text-sm font-semibold text-success">~2.5min</div>
              <div className="text-xs text-muted-foreground">Avg per File</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchControls;
